import { useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useAddSecondaryResource,
  useRemoveSecondaryResource,
  useResources,
  useResourceRoles,
  useTicketSecondaryResources,
  type Resource,
  type TicketSecondaryResource,
} from '@/api/hooks';

interface Props {
  ticketId: number;
  /** The primary assignee — already on the ticket, so not offered as a secondary. */
  assignedResourceId: number | null;
  className?: string;
}

function resourceName(r: Pick<Resource, 'firstName' | 'lastName'>): string {
  return `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim();
}

/**
 * Secondary resources on a ticket (`TicketSecondaryResources`) — the other engineers working
 * it alongside the assignee. Adding one needs a role, exactly like the primary assignment,
 * so this uses the same two-step person → role flow as AssignedResourcePicker.
 */
export function SecondaryResourcesPicker({ ticketId, assignedResourceId, className }: Props) {
  const links = useTicketSecondaryResources(ticketId);
  const resources = useResources();
  const add = useAddSecondaryResource();
  const remove = useRemoveSecondaryResource();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<{ id: number; label: string } | null>(null);

  // Roles for the person picked in step 1 — the server would fall back to their first role,
  // but a ticket's secondary resource is usually deliberate about which hat they're wearing.
  const roles = useResourceRoles(pending?.id ?? null);

  const linked = links.data?.items ?? [];
  const alreadyOn = new Set([
    ...linked.map((l) => l.resourceID),
    ...(assignedResourceId != null ? [assignedResourceId] : []),
  ]);

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (resources.data?.items ?? [])
      .filter((r) => !alreadyOn.has(r.id))
      .filter((r) => !q || resourceName(r).toLowerCase().includes(q) || (r.email ?? '').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources.data, query, linked, assignedResourceId]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) { setQuery(''); setPending(null); }
  };

  const addWithRole = (roleID: number) => {
    if (!pending) return;
    const { id, label } = pending;
    add.mutate(
      { ticketId, resourceID: id, roleID },
      {
        onSuccess: () => { toast.success(`Added ${label}`); setOpen(false); setPending(null); },
        onError: (err) => toast.error('Failed to add resource', {
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
      },
    );
  };

  const removeResource = (linkId: number, label: string) => {
    remove.mutate(
      { ticketId, linkId },
      {
        onSuccess: () => toast.success(`Removed ${label}`),
        onError: (err) => toast.error('Failed to remove resource', {
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
      },
    );
  };

  return (
    <div className={cn('flex flex-col items-end gap-1', className)}>
      {linked.map((link) => (
        <SecondaryResourceRow
          key={link.id}
          link={link}
          name={
            resourceName(
              resources.data?.items.find((r) => r.id === link.resourceID) ?? { firstName: '', lastName: '' },
            ) || `#${link.resourceID}`
          }
          onRemove={removeResource}
          removing={remove.isPending}
        />
      ))}

      {linked.length === 0 && !links.isLoading && (
        <span className="text-sm text-muted-foreground">None</span>
      )}
      {links.isLoading && <span className="text-sm text-muted-foreground">Loading…</span>}

      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger
          disabled={add.isPending}
          className={cn(
            'flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-muted-foreground',
            'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:opacity-40',
          )}
        >
          {add.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Add
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64 overflow-hidden p-0">
          {pending == null ? (
            <>
              <div className="p-2">
                <Input
                  autoFocus
                  placeholder="Search by name or email…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-8"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <DropdownMenuSeparator className="my-0" />
              <div className="max-h-72 overflow-y-auto overflow-x-hidden py-1">
                {resources.isLoading && <DropdownMenuItem disabled>Loading resources…</DropdownMenuItem>}
                {!resources.isLoading && options.length === 0 && (
                  <DropdownMenuItem disabled>No resources left to add</DropdownMenuItem>
                )}
                {options.map((r) => (
                  <DropdownMenuItem
                    key={r.id}
                    onSelect={(e) => { e.preventDefault(); setPending({ id: r.id, label: resourceName(r) }); }}
                    className="truncate"
                  >
                    <span className="truncate">{resourceName(r)}</span>
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 px-2 py-2">
                <button
                  onClick={() => setPending(null)}
                  className="rounded p-0.5 hover:bg-accent"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <span className="truncate text-sm font-medium">{pending.label}</span>
              </div>
              <DropdownMenuSeparator className="my-0" />
              <div className="px-2 py-1 text-xs text-muted-foreground">Select role</div>
              <div className="max-h-64 overflow-y-auto overflow-x-hidden py-1">
                {roles.isLoading && (
                  <DropdownMenuItem disabled>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />Loading roles…
                  </DropdownMenuItem>
                )}
                {!roles.isLoading && (roles.data?.items ?? []).length === 0 && (
                  <DropdownMenuItem disabled>No roles found</DropdownMenuItem>
                )}
                {(roles.data?.items ?? []).map((role) => (
                  <DropdownMenuItem key={role.id} onSelect={() => addWithRole(role.id)}>
                    {role.name}
                  </DropdownMenuItem>
                ))}
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * One assigned row. Its own component so the role-name lookup is a hook per row rather than
 * a hook inside a loop.
 */
function SecondaryResourceRow({
  link,
  name,
  onRemove,
  removing,
}: {
  link: TicketSecondaryResource;
  name: string;
  onRemove: (linkId: number, label: string) => void;
  removing: boolean;
}) {
  const roles = useResourceRoles(link.resourceID);
  const roleName = roles.data?.items.find((r) => r.id === link.roleID)?.name ?? null;

  return (
    <div className="flex max-w-full items-start gap-1">
      <span className="flex min-w-0 flex-col items-end">
        <span className="truncate text-sm">{name}</span>
        {roleName && <span className="truncate text-[11px] text-muted-foreground">{roleName}</span>}
      </span>
      <button
        type="button"
        onClick={() => onRemove(link.id, name)}
        disabled={removing}
        className="mt-[3px] shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
        aria-label={`Remove ${name}`}
        title="Remove"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
